/**
 * Colour math utilities for the custom theme builder.
 * All hex values are lowercase 6-digit strings like '#1a2b3c'.
 */

/** Parse a 3- or 6-digit hex string to {r, g, b} (0–255 each). Returns null on invalid input. */
export function hexToRgb(hex) {
  if (!hex) return null;
  const s = hex.replace('#', '');
  if (s.length === 3) {
    const r = parseInt(s[0] + s[0], 16);
    const g = parseInt(s[1] + s[1], 16);
    const b = parseInt(s[2] + s[2], 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
    return { r, g, b };
  }
  if (s.length === 6) {
    const r = parseInt(s.slice(0, 2), 16);
    const g = parseInt(s.slice(2, 4), 16);
    const b = parseInt(s.slice(4, 6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
    return { r, g, b };
  }
  return null;
}

/** Convert {r, g, b} (0–255 each) to a 7-char lowercase hex string. */
export function rgbToHex({ r, g, b }) {
  return '#' + [r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('');
}

/**
 * WCAG 2.1 relative luminance.
 * Accepts {r, g, b} in 0–255 range.
 */
export function relativeLuminance({ r, g, b }) {
  const linearise = (c) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * linearise(r) + 0.7152 * linearise(g) + 0.0722 * linearise(b);
}

/**
 * WCAG contrast ratio between two hex colours.
 * Returns null if either hex is invalid.
 */
export function contrastRatio(hex1, hex2) {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  if (!rgb1 || !rgb2) return null;
  const l1 = relativeLuminance(rgb1);
  const l2 = relativeLuminance(rgb2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * WCAG 2.2 compliance level for a contrast ratio.
 * Returns 'AAA', 'AA', 'AA-large' (passes only for large text / UI), 'fail',
 * 'unreadable' (ratio < 2 — hard to read even with perfect vision), or null.
 */
export function wcagLevel(ratio) {
  if (ratio === null) return null;
  if (ratio >= 7) return 'AAA';
  if (ratio >= 4.5) return 'AA';
  if (ratio >= 3) return 'AA-large';
  if (ratio >= 2) return 'fail';
  return 'unreadable';
}

/**
 * Convert HSV to {r, g, b} (0–255 each).
 * h: 0–360, s: 0–1, v: 0–1
 */
export function hsvToRgb(h, s, v) {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60)       { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else              { r = c; g = 0; b = x; }
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

/**
 * Convert {r, g, b} (0–255 each) to {h, s, v}.
 * h: 0–360, s: 0–1, v: 0–1
 */
export function rgbToHsv({ r, g, b }) {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h = ((h * 60) + 360) % 360;
  }
  const s = max === 0 ? 0 : d / max;
  return { h, s, v: max };
}

/**
 * Adjust a foreground hex colour to achieve a minimum contrast ratio against a background,
 * keeping hue and saturation constant and changing brightness (V in HSV) by the minimum
 * possible amount. Tries both lightening and darkening and returns whichever requires
 * the smaller change from the original colour.
 *
 * Returns the adjusted hex, or the original if the target cannot be achieved even at
 * pure black/white (extremely rare).
 */
export function adjustForContrast(fgHex, bgHex, targetRatio) {
  const fgRgb = hexToRgb(fgHex);
  if (!fgRgb || !hexToRgb(bgHex)) return fgHex;

  // Already meets target — nothing to do
  if ((contrastRatio(fgHex, bgHex) ?? 0) >= targetRatio) return fgHex;

  const { h, s, v } = rgbToHsv(fgRgb);

  // Contrast at a given brightness V (hue and saturation fixed)
  function contrastAtV(val) {
    return contrastRatio(rgbToHex(hsvToRgb(h, s, val)), bgHex) ?? 0;
  }

  // Binary-search for the minimum V in direction [start → extreme] that
  // achieves targetRatio. Returns the adjusted V or null if impossible.
  function searchV(start, extreme) {
    if (contrastAtV(extreme) < targetRatio) return null; // can't achieve even at limit
    const goUp = extreme > start;
    let lo = goUp ? start : extreme;
    let hi = goUp ? extreme : start;
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      if (contrastAtV(mid) >= targetRatio) {
        // Mid works — try moving back toward start (less change)
        if (goUp) hi = mid; else lo = mid;
      } else {
        // Mid doesn't work — need to go further from start
        if (goUp) lo = mid; else hi = mid;
      }
    }
    const result = goUp ? hi : lo;
    return contrastAtV(result) >= targetRatio ? result : null;
  }

  const vLighter = searchV(v, 1.0); // try brightening toward white
  const vDarker  = searchV(v, 0.0); // try darkening toward black

  // Pick whichever direction requires the smaller change in V
  const deltaLighter = vLighter !== null ? vLighter - v : Infinity;
  const deltaDarker  = vDarker  !== null ? v - vDarker  : Infinity;

  if (deltaLighter <= deltaDarker && vLighter !== null) {
    return rgbToHex(hsvToRgb(h, s, vLighter));
  }
  if (vDarker !== null) {
    return rgbToHex(hsvToRgb(h, s, vDarker));
  }
  return fgHex;
}

/**
 * Given an action-background hex, decide whether the foreground (text on it) should be
 * black or white. Uses the WCAG luminance threshold approach.
 */
export function deriveActionFg(actionBgHex) {
  const rgb = hexToRgb(actionBgHex);
  if (!rgb) return '#ffffff';
  const lum = relativeLuminance(rgb);
  // If luminance > 0.179, black text gives higher contrast; otherwise white.
  return lum > 0.179 ? '#000000' : '#ffffff';
}

/**
 * Given the user-customisable vars, return the full set of CSS custom property
 * key-value pairs (including auto-derived ones) to apply to the theme.
 *
 * If --c-action-fg is present in userVars, the user's explicit value is used;
 * otherwise it is auto-derived from --c-action-bg luminance.
 * --c-focus is always derived from --c-action-bg.
 */
export function buildCustomThemeVars(userVars) {
  const actionBg = userVars['--c-action-bg'] || '#7c3aed';
  return {
    ...userVars,
    '--c-action-fg': userVars['--c-action-fg'] ?? deriveActionFg(actionBg),
    '--c-focus':     actionBg,
  };
}

/**
 * The 14 variable keys that users can customise, in display order.
 * Each entry has a key, a display label key (i18n), and which other variable it
 * is typically rendered on top of (for contrast checking).
 *
 * Note: --c-action-fg defaults to auto-derived from --c-action-bg luminance but is
 * editable; changing --c-action-bg in the ThemeEditor auto-updates --c-action-fg.
 * --c-focus is always auto-derived from --c-action-bg and not editable.
 * --c-text-disabled is intentionally below AA (disabled state) so it is included
 * for editing but excluded from contrast pair checks.
 */
export const CUSTOMISABLE_VARS = [
  { key: '--c-bg-page',        labelKey: 'customTheme.vars.bgPage',       onTop: null },
  { key: '--c-bg-surface',     labelKey: 'customTheme.vars.bgSurface',    onTop: '--c-bg-page' },
  { key: '--c-bg-input',       labelKey: 'customTheme.vars.bgInput',      onTop: '--c-bg-surface' },
  { key: '--c-border',         labelKey: 'customTheme.vars.border',       onTop: '--c-bg-surface' },
  { key: '--c-text',           labelKey: 'customTheme.vars.text',         onTop: '--c-bg-surface' },
  { key: '--c-text-secondary', labelKey: 'customTheme.vars.textSecondary',onTop: '--c-bg-surface' },
  { key: '--c-text-muted',     labelKey: 'customTheme.vars.textMuted',    onTop: '--c-bg-surface' },
  { key: '--c-text-disabled',  labelKey: 'customTheme.vars.textDisabled', onTop: '--c-bg-surface' },
  { key: '--c-text-accent',    labelKey: 'customTheme.vars.textAccent',   onTop: '--c-bg-surface' },
  { key: '--c-action-bg',      labelKey: 'customTheme.vars.actionBg',     onTop: '--c-bg-surface' },
  { key: '--c-action-fg',      labelKey: 'customTheme.vars.actionFg',     onTop: '--c-action-bg' },
  { key: '--c-success',        labelKey: 'customTheme.vars.success',      onTop: '--c-bg-surface' },
  { key: '--c-error',          labelKey: 'customTheme.vars.error',        onTop: '--c-bg-surface' },
  { key: '--c-warning',        labelKey: 'customTheme.vars.warning',      onTop: '--c-bg-surface' },
  { key: '--c-highlight',      labelKey: 'customTheme.vars.highlight',    onTop: '--c-bg-surface' },
];

/**
 * Collect all contrast pairs that should be checked for a given set of vars.
 * Returns an array of { fg, bg, fgKey, bgKey, ratio, level } objects.
 * Only pairs where both keys are present in vars are included.
 */
export function checkContrastPairs(vars) {
  const pairs = [
    { fgKey: '--c-text',           bgKey: '--c-bg-page' },
    { fgKey: '--c-text',           bgKey: '--c-bg-surface' },
    { fgKey: '--c-text',           bgKey: '--c-bg-input' },
    { fgKey: '--c-text-secondary', bgKey: '--c-bg-surface' },
    { fgKey: '--c-text-muted',     bgKey: '--c-bg-surface' },
    { fgKey: '--c-text-accent',    bgKey: '--c-bg-surface' },
    { fgKey: '--c-border',         bgKey: '--c-bg-surface' },
    { fgKey: '--c-action-fg',      bgKey: '--c-action-bg' },
    // status colours on surface (--c-text-disabled intentionally below AA, so omitted)
    { fgKey: '--c-success',        bgKey: '--c-bg-surface' },
    { fgKey: '--c-error',          bgKey: '--c-bg-surface' },
    { fgKey: '--c-warning',        bgKey: '--c-bg-surface' },
  ];

  const allVars = buildCustomThemeVars(vars);

  return pairs.map(({ fgKey, bgKey, derived }) => {
    const fg = allVars[fgKey];
    const bg = allVars[bgKey];
    if (!fg || !bg) return null;
    const ratio = contrastRatio(fg, bg);
    return { fgKey, bgKey, fg, bg, ratio, level: wcagLevel(ratio), derived: derived || false };
  }).filter(Boolean);
}
